import {ObservableMap} from "mobx";
import MdEntity from "core/metamodel/mdEntity";
import {SecurableDefinitionModuleMap, SecurablePermission, SecurityRole} from "core/security/securityDomain";
import {observer} from "mobx-react";
import React from "react";
import {DragSource} from 'react-dnd';
import Drawer from "material-ui/Drawer";
import FlatButton from "material-ui/FlatButton";
import {addSeparator} from "core/common/utils/arrayUtils";
import {MdDivider} from "core/components/blockComp/blockComp";
import {getLovItem, lovLabel, lovParam, lovValue} from "core/form/formUtils";
import {MODULE, PERMISSION, SECURABLE_TYPE, TSECURABLE} from "core/metamodel/metaConstant";
import Chip from "material-ui/Chip";
import {createIfAbsent} from "core/common/utils/jsonUtils";


interface  SecurableDrawerProps {
  entities: ObservableMap<MdEntity>;
  open: boolean;
  onRequestChange: any;
  securables: SecurableDefinitionModuleMap;
  securityRoleObject: SecurityRole;
}

@observer
export class SecurableDrawer extends React.Component<SecurableDrawerProps, undefined> {

  render() {

    let {entities, open, onRequestChange, securables, securityRoleObject} = this.props;

    let screenWidth = document.body.getBoundingClientRect().width;

    return (
      <Drawer open={open}
              width={0.5 * screenWidth}
              docked
              onRequestChange={onRequestChange}
              containerStyle={{paddingTop: 40}}
      >

        <div style={{margin: 20}}>
          <FlatButton label="Close" primary onClick={onRequestChange}/>
        </div>

        {
          securables &&
          addSeparator(Object.entries(pruneSecurables(securables, securityRoleObject.permissions)).map((pair, index) => (
            <ModuleSecurablesBlock module={pair[0] as MODULE} moduleSecurables={pair[1]} entities={entities} key={index}/>
          )), <MdDivider inset/>)
        }

      </Drawer>
    )

  }

}


type TPermissionSecurableMap = {[key in TSECURABLE]: PERMISSION[]}
type TPermissionSecurableTypeMap = {[key in SECURABLE_TYPE]: TPermissionSecurableMap}
type TPermissionModuleMap = Partial<{[key in MODULE]: TPermissionSecurableTypeMap}>

interface ModuleSecurablesBlockProps {
  moduleSecurables?: TPermissionSecurableTypeMap;
  module: MODULE;
  entities?: ObservableMap<MdEntity>;
}

const ModuleSecurablesBlock = observer(({moduleSecurables, module, entities}: ModuleSecurablesBlockProps) => {

  const moduleLov = getLovItem(entities.get('MODULE').lovItems, module);

  return (
    <div className="secu-module">
      <div className="secu-module-header">
        <span className={lovParam(moduleLov, 'icon')}/>
        <div className="text">{lovLabel(moduleLov)}</div>
      </div>

      <div className="secu-module-body">
        {addSeparator(Object.entries(moduleSecurables)
          .map((pair, index) => <TypeSecurablesBlock module={module} entities={entities} type={pair[0] as SECURABLE_TYPE} typeSecurables={pair[1]} key={index} />), <MdDivider/>)}
      </div>
    </div>
  );
});



interface TypeSecurablesBlockProps {
  typeSecurables?: TPermissionSecurableMap;
  type?: SECURABLE_TYPE;
  module: MODULE;
  entities?: ObservableMap<MdEntity>;
}

const TypeSecurablesBlock = observer(({typeSecurables, type, entities, module}: TypeSecurablesBlockProps) => {

  const typeLov = getLovItem(entities.get('SECURABLETYPE').lovItems, type);

  return (
    <div className="secu-type">
      <div className="secu-type-header">
        {lovLabel(typeLov)}
      </div>
      <div className="secu-type-body">
        {Object.entries(typeSecurables)
          .map((pair, index) => <DraggableSecurableBlock module={module} entities={entities} securable={pair[0]} key={index} />)}
      </div>
    </div>

  );

});


const _secu_securable_styles = {margin: 3};
const _label_style = {fontSize: 13};

interface SecurableBlockProps {
  entities: ObservableMap<MdEntity>;
  securable: string;
  isDragging?: any
  connectDragSource?: any;
  module?: MODULE;
}

@observer
class SecurableBlock extends React.Component<SecurableBlockProps, undefined> {


  render() {
    // Your component receives its own props as usual
    const { entities,securable, isDragging, connectDragSource} = this.props;
    const opacity = isDragging ? 0.4 : 1;

    const bodyEl = (
      <div style={{..._secu_securable_styles, opacity}}>
        <Chip style={{cursor: 'move'}}>
          {lovLabel(getLovItem(entities.get('SECURABLE').lovItems, securable))}
        </Chip>
      </div>
    );

    return connectDragSource(bodyEl);
  }
}

/**
 * Specifies the drag source contract.
 * Only `beginDrag` function is required.
 */
const securableSource = {
  beginDrag(props) {
    console.log('begin drag');
    // Return the data describing the dragged item
    const item = { module: props.module, type: props.type, securable: props.securable };
    return item;
  },

  endDrag(props, monitor, component) {
    if (!monitor.didDrop()) {
      return;
    }

    // When dropped on a compatible target, do something
    const item = monitor.getItem();
    const dropResult = monitor.getDropResult();
    console.log('endDrag: item = %O, dropResult = %O, props = %O, component = %O', item, dropResult, props, component);
    //CardActions.moveCardToList(item.id, dropResult.listId);
  }
};


const DraggableSecurableBlock = DragSource("SECURABLE", securableSource, (connect, monitor) => ({
  // Call this function inside render()
  // to let React DnD handle the drag events:
  connectDragSource: connect.dragSource(),
  // You can ask the monitor about the current drag state:
  isDragging: monitor.isDragging()
}))(SecurableBlock);


function pruneSecurables(securableDefinitionModuleMap: SecurableDefinitionModuleMap, permissions: SecurablePermission[]): TPermissionModuleMap {
  const mapa: TPermissionModuleMap = {};
  let securableStrings: string[] = permissions.map(pObj => lovValue(pObj.securable));

  Object.values(securableDefinitionModuleMap).forEach(securableDefinitionSecurableMap => {
    Object.values(securableDefinitionSecurableMap).forEach(securableDefinition => {
      if (!securableStrings.includes(lovValue(securableDefinition.securable))) {
        getOrCreateSecurable(mapa, lovValue(securableDefinition.module), lovValue(securableDefinition.type), lovValue(securableDefinition.securable), sec => securableDefinition.availablePermissions)
      }
    })
  })

  return mapa;
}

function getOrCreateSecurable(permissionMap, module: MODULE, type: SECURABLE_TYPE, securable: TSECURABLE, fn) {
  const mapOfModule = createIfAbsent(permissionMap, module, (x) => ({}) );
  const mapOfType = createIfAbsent(mapOfModule, type, (x) => ({}) );
  return createIfAbsent(mapOfType, securable, fn );
}



// WEBPACK FOOTER //
// ./src/core/security/component/SecurableDrawer.tsx